import { Identity } from '@/containers/Identities/types';
import { MediaItem } from '@/containers/MediaManager/types';
import { replaceRawAttributes } from '@/containers/TextEditor/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import {
  AIOptOutThreshold,
  InitialMessageTextAndHtml,
  InitialMessageValidationItem,
  JobAgentAssignment,
  JobDetailsResponse,
  JobPhoneListAssignment,
  JobQuestion,
  JobSelectionType,
  JobSuppressionListAssignment,
  JobTemplate,
  JobTrackedLink,
} from '../types';

export type JobEditorState = {
  mode: 'new' | 'edit';
  jobId: string;
  originalJobData?: JobDetailsResponse;
  isDirty: boolean;
  jobName?: string;
  jobArea?: string;
  // Subset of NPA (area) codes for the selected state. Empty array (or
  // undefined) means "all area codes for the state". Ignored when jobArea is
  // 'USA' or 'CAN' (national).
  didNpaSubset?: string[];
  billingId?: string;
  initialMessageText?: string;
  initialMessageHtml?: string;
  initialMessageOptOutText?: string;
  initialMessageMaxLength?: number;
  initialMessageValidationMessages: InitialMessageValidationItem[];
  initialMessageMedia?: MediaItem;
  jobType?: JobSelectionType;
  identity_id: Identity['identity_id'];
  assignedContactLists: JobPhoneListAssignment[];
  assignedAgents: JobAgentAssignment[];
  trackedLinks: JobTrackedLink[];
  isNewAgentCreated?: boolean;
  autoAssignAgent?: JobAgentAssignment;
  linkToAddToInitialMessage?: JobTrackedLink;
  assignedSuppressionLists: JobSuppressionListAssignment[];
  questions: JobQuestion[];
  isNewSuppressionListCreated?: boolean;
  activeEntrySectionId?: string;
  isShowingFloatingAttributes?: boolean;
  isShowingFloatingEmoji?: boolean;
  isShowingMediaManager?: boolean;
  isAiEnabled?: boolean;
  aiAutoOptOutThreshold?: AIOptOutThreshold;
  usesRateplan?: boolean;
  validationState: {
    isValid: boolean;
    errors: {
      rootAccount?: string;
      jobName?: string;
      initialMessage?: string[];
      serverMessage?: string;
    };
    isValidating: boolean;
  };
};

export type JobEditorsState = {
  [jobId: string]: JobEditorState;
};

const getInitialJobEditorState = (
  mode: 'new' | 'edit' = 'new',
  jobId: string = ''
): JobEditorState => ({
  mode,
  jobId,
  isDirty: false,
  originalJobData: undefined,
  jobName: undefined,
  jobArea: undefined,
  didNpaSubset: undefined,
  billingId: undefined,
  jobType: undefined,
  identity_id: '',
  assignedContactLists: [],
  assignedAgents: [],
  trackedLinks: [],
  linkToAddToInitialMessage: undefined,
  isNewAgentCreated: false,
  autoAssignAgent: undefined,
  assignedSuppressionLists: [],
  questions: [],
  isNewSuppressionListCreated: false,
  isShowingFloatingAttributes: false,
  isShowingFloatingEmoji: false,
  isShowingMediaManager: false,
  activeEntrySectionId: undefined,
  initialMessageText: undefined,
  initialMessageHtml: undefined,
  initialMessageOptOutText: undefined,
  initialMessageMaxLength: undefined,
  initialMessageMedia: undefined,
  initialMessageValidationMessages: [],
  isAiEnabled: false,
  aiAutoOptOutThreshold: undefined,
  usesRateplan: false,
  validationState: {
    isValid: false,
    errors: {},
    isValidating: false,
  },
});

const initialState: JobEditorsState = {};

const getNextQuestionOrder = (state: JobEditorState) => {
  const questions = state.questions;
  const latestOrder = questions?.length
    ? Math.max(...questions.map((q) => q.order ?? 0))
    : 0;
  return latestOrder + 1;
};

const getNextTrackedLinkId = (state: JobEditorState) => {
  return state.trackedLinks.length + 1;
};

const markDirty = (state: JobEditorState) => {
  state.isDirty = true;
};

export const jobEditorsSlice = createSlice({
  name: 'jobEditors',
  initialState,
  reducers: {
    initializeJobEditor: (
      state: JobEditorsState,
      action: PayloadAction<{
        jobId: string;
        mode: 'new' | 'edit';
        initialData?: JobDetailsResponse;
      }>
    ) => {
      const { jobId, mode, initialData } = action.payload;
      if (!state[jobId]) {
        state[jobId] = getInitialJobEditorState(mode, jobId);
        if (initialData) {
          state[jobId].originalJobData = initialData;
        }
      }
    },
    cleanupJobEditor: (state: JobEditorsState, action: PayloadAction<string>) => {
      const jobId = action.payload;
      delete state[jobId];
    },
    populateFromJobDetails: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; jobDetails: JobDetailsResponse }>
    ) => {
      const { jobId, jobDetails } = action.payload;

      if (!state[jobId]) {
        state[jobId] = getInitialJobEditorState('edit', jobId);
      }

      const editor = state[jobId];
      editor.originalJobData = jobDetails;

      // Map JobDetailsResponse to JobEditorState
      editor.jobName = jobDetails.name;
      editor.identity_id = jobDetails.identity_id;
      editor.jobArea = jobDetails.did_state;
      editor.didNpaSubset = jobDetails.did_npa_subset ?? [];
      editor.billingId = jobDetails.external_billing_id || '';
      editor.isAiEnabled = jobDetails.ai_enabled;
      editor.aiAutoOptOutThreshold = jobDetails.ai_auto_opt_out_threshold;

      // Map agents
      editor.assignedAgents = Object.values(jobDetails.agents || {}).map((agent) => ({
        agent_id: agent.id,
        isResponder: agent.is_responder,
      }));

      // Map contact lists
      editor.assignedContactLists =
        jobDetails.phone_lists_details?.map((list) => ({
          list_id: list.list_id,
          deduplicate: true, // Default value, may need adjustment
        })) || [];

      // Map suppression lists
      editor.assignedSuppressionLists =
        jobDetails.suppression_lists_details?.map((list) => ({
          suppression_list_id: list.suppression_list_id,
          create_results_phone_list: false, // Default value, may need adjustment
        })) || [];

      // Map tracked links
      editor.trackedLinks = jobDetails.tracked_links || [];

      // Map questions
      editor.questions = jobDetails.questions || [];

      // Map initial message from default template
      const defaultTemplate = jobDetails.templates?.find((t) => t.is_default);

      if (defaultTemplate) {
        const templateText = defaultTemplate.text || '';
        editor.initialMessageText = templateText;
        // Process the template text to convert raw attributes to HTML tags
        const processedHtml = templateText ? replaceRawAttributes(templateText) : '';
        editor.initialMessageHtml = processedHtml;

        // Map initial message media from default template (convert JobTemplateMedia to MediaItem)
        if (defaultTemplate.media) {
          editor.initialMessageMedia = {
            media_id: defaultTemplate.media.media_id,
            account_id: jobDetails.account_id || '',
            identity_id: jobDetails.identity_id,
            created_date: '',
            created_by: '',
            last_touched_date: '',
            last_touched_by: '',
            title: defaultTemplate.media.title,
            status: 'ACTIVE' as const,
            error: null,
            initial_file_upload: null,
            media_type: defaultTemplate.media.media_type,
            mime_type: (defaultTemplate.media.mime_type || 'image/png') as 'image/png',
            size_bytes: 0,
            duration_ms: null,
            preview_url: defaultTemplate.media.preview_url,
            thumbnail_url: defaultTemplate.media.thumbnail_url,
            transcode_start_date: null,
            video_poster_url: null,
            video_subtitles: null,
          };
        }
      }

      // Reset dirty state since we're loading existing data
      editor.isDirty = false;
    },
    markClean: (state: JobEditorsState, action: PayloadAction<string>) => {
      const jobId = action.payload;
      if (state[jobId]) {
        state[jobId].isDirty = false;
      }
    },
    resetJobEditor: (state: JobEditorsState, action: PayloadAction<string>) => {
      const jobId = action.payload;
      if (state[jobId]) {
        const { mode, originalJobData } = state[jobId];
        if (mode === 'edit' && originalJobData) {
          // Reset to original data for edit mode
          const newState = getInitialJobEditorState(mode, jobId);
          newState.originalJobData = originalJobData;
          state[jobId] = newState;
          // Re-populate from original data would happen here
        } else {
          // Reset to initial state for new mode
          state[jobId] = getInitialJobEditorState(mode, jobId);
        }
      }
    },
    reset: (state: JobEditorsState, action: PayloadAction<string>) => {
      // For backwards compatibility with existing reset calls
      const jobId = action.payload;
      if (state[jobId]) {
        state[jobId] = getInitialJobEditorState(state[jobId].mode, jobId);
      }
    },
    setIdentityId: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; identity_id: Identity['identity_id'] }>
    ) => {
      const { jobId, identity_id } = action.payload;
      if (state[jobId]) {
        const previousIdentityId = state[jobId].identity_id;
        state[jobId].identity_id = identity_id;

        // Only clear assigned data if the identity actually changed
        if (previousIdentityId !== identity_id) {
          state[jobId].assignedAgents = [];
          state[jobId].assignedContactLists = [];
          state[jobId].assignedSuppressionLists = [];
          state[jobId].questions = [];
          markDirty(state[jobId]);
        }
      }
    },
    setJobName: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; jobName: string }>
    ) => {
      const { jobId, jobName } = action.payload;
      if (state[jobId]) {
        state[jobId].jobName = jobName || '';
        markDirty(state[jobId]);
      }
    },
    setJobArea: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; jobArea: string }>
    ) => {
      const { jobId, jobArea } = action.payload;
      if (state[jobId]) {
        const newArea = jobArea || '';
        // Reset area code selections whenever the state changes so we don't
        // submit codes that don't belong to the new state.
        if (state[jobId].jobArea !== newArea) {
          state[jobId].didNpaSubset = [];
        }
        state[jobId].jobArea = newArea;
        markDirty(state[jobId]);
      }
    },
    setDidNpaSubset: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; didNpaSubset: string[] }>
    ) => {
      const { jobId, didNpaSubset } = action.payload;
      if (state[jobId]) {
        state[jobId].didNpaSubset = didNpaSubset;
        markDirty(state[jobId]);
      }
    },
    setBillingId: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; billingId: string }>
    ) => {
      const { jobId, billingId } = action.payload;
      if (state[jobId]) {
        state[jobId].billingId = billingId || '';
        markDirty(state[jobId]);
      }
    },
    setJobType: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; jobType: JobSelectionType }>
    ) => {
      const { jobId, jobType } = action.payload;
      if (state[jobId]) {
        state[jobId].jobType = jobType;
        markDirty(state[jobId]);
      }
    },
    setIsAiEnabled: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; isAiEnabled: boolean }>
    ) => {
      const { jobId, isAiEnabled } = action.payload;
      if (state[jobId]) {
        state[jobId].isAiEnabled = isAiEnabled;
        markDirty(state[jobId]);
      }
    },
    setAIOptOutThreshold: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; threshold: AIOptOutThreshold }>
    ) => {
      const { jobId, threshold } = action.payload;
      if (state[jobId]) {
        state[jobId].aiAutoOptOutThreshold = threshold;
        markDirty(state[jobId]);
      }
    },
    setUsesRateplan: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; usesRateplan: boolean }>
    ) => {
      const { jobId, usesRateplan } = action.payload;
      if (state[jobId]) {
        state[jobId].usesRateplan = usesRateplan;
        markDirty(state[jobId]);
      }
    },
    setActiveEntrySectionId: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; sectionId?: string }>
    ) => {
      const { jobId, sectionId } = action.payload;
      if (state[jobId]) {
        state[jobId].activeEntrySectionId = sectionId;
      }
    },
    setInitialMessageMedia: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; media?: MediaItem }>
    ) => {
      const { jobId, media } = action.payload;
      if (state[jobId]) {
        state[jobId].initialMessageMedia = media;
        markDirty(state[jobId]);
      }
    },
    setInitialMessageContent: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; content: InitialMessageTextAndHtml }>
    ) => {
      const { jobId, content } = action.payload;
      if (state[jobId]) {
        state[jobId].initialMessageText = content.messageText;
        state[jobId].initialMessageHtml = content.messageHtml;
        markDirty(state[jobId]);
      }
    },
    setInitialMessageOptOutText: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; optOutText?: string }>
    ) => {
      const { jobId, optOutText } = action.payload;
      if (state[jobId]) {
        state[jobId].initialMessageOptOutText = optOutText;
        markDirty(state[jobId]);
      }
    },
    setInitialMessageMaxLength: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; maxLength?: number }>
    ) => {
      const { jobId, maxLength } = action.payload;
      if (state[jobId]) {
        state[jobId].initialMessageMaxLength = maxLength;
        markDirty(state[jobId]);
      }
    },
    setInitialMessageValidationMessages: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; messages: InitialMessageValidationItem[] }>
    ) => {
      const { jobId, messages } = action.payload;
      if (state[jobId]) {
        state[jobId].initialMessageValidationMessages = messages;
      }
    },
    setIsShowingFloatingAttributes: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; isShowing: boolean }>
    ) => {
      const { jobId, isShowing } = action.payload;
      if (state[jobId]) {
        state[jobId].isShowingFloatingAttributes = isShowing;
      }
    },
    setIsShowingFloatingEmoji: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; isShowing: boolean }>
    ) => {
      const { jobId, isShowing } = action.payload;
      if (state[jobId]) {
        state[jobId].isShowingFloatingEmoji = isShowing;
      }
    },
    setIsShowingMediaManager: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; isShowing: boolean }>
    ) => {
      const { jobId, isShowing } = action.payload;
      if (state[jobId]) {
        state[jobId].isShowingMediaManager = isShowing;
      }
    },

    // Contact Lists assignment
    setAssignContactList: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; assignment: JobPhoneListAssignment }>
    ) => {
      const { jobId, assignment } = action.payload;
      if (state[jobId]) {
        const ids = state[jobId].assignedContactLists.map((c) => c.list_id);
        if (!ids.includes(assignment.list_id)) {
          state[jobId].assignedContactLists.push(assignment);
          markDirty(state[jobId]);
        }
      }
    },
    setUnassignContactList: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; listId: number }>
    ) => {
      const { jobId, listId } = action.payload;
      if (state[jobId]) {
        state[jobId].assignedContactLists = state[jobId].assignedContactLists.filter(
          (item) => item.list_id !== listId
        );
        markDirty(state[jobId]);
      }
    },

    // Tracked Links
    addTrackedLink: (
      state: JobEditorsState,
      action: PayloadAction<{
        jobId: string;
        data: Pick<
          JobTrackedLink,
          | 'is_custom_slash_tag_link'
          | 'destination_url'
          | 'domain_id'
          | 'domain_name'
          | 'slash_tag'
        >;
        autoAssign?: boolean;
      }>
    ) => {
      const { jobId, data, autoAssign } = action.payload;
      if (state[jobId]) {
        const id = getNextTrackedLinkId(state[jobId]);
        const message_variable = data.is_custom_slash_tag_link
          ? `https://${data.domain_name}/${data.slash_tag}`
          : `{tracked_link_${id}}`;

        const trackedLink: JobTrackedLink = {
          ...data,
          is_deleted: false,
          id,
          message_variable,
        };

        state[jobId].trackedLinks.push(trackedLink);
        if (autoAssign) {
          state[jobId].linkToAddToInitialMessage = trackedLink;
        }
        markDirty(state[jobId]);
      }
    },
    removeTrackedLink: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; linkId: number }>
    ) => {
      const { jobId, linkId } = action.payload;
      if (state[jobId]) {
        state[jobId].trackedLinks = state[jobId].trackedLinks.filter(
          (item) => item.id !== linkId
        );
        markDirty(state[jobId]);
      }
    },
    updateTrackedLink: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; id: number; props: Partial<JobTrackedLink> }>
    ) => {
      const { jobId, id, props } = action.payload;
      if (state[jobId]) {
        state[jobId].trackedLinks = state[jobId].trackedLinks.map((a) => {
          if (a.id === id) {
            return {
              ...a,
              ...props,
            };
          }
          return a;
        });
        markDirty(state[jobId]);
      }
    },
    addLinkToInitialMessage: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; link?: JobTrackedLink }>
    ) => {
      const { jobId, link } = action.payload;
      if (state[jobId]) {
        state[jobId].linkToAddToInitialMessage = link;
      }
    },

    // Agents assignment
    setAssignAgentAutomatically: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; agent?: JobAgentAssignment }>
    ) => {
      const { jobId, agent } = action.payload;
      if (state[jobId]) {
        state[jobId].autoAssignAgent = agent;
      }
    },
    setIsNewAgentCreated: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; isCreated: boolean }>
    ) => {
      const { jobId, isCreated } = action.payload;
      if (state[jobId]) {
        state[jobId].isNewAgentCreated = isCreated;
      }
    },
    setAssignAgent: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; assignment: JobAgentAssignment }>
    ) => {
      const { jobId, assignment } = action.payload;
      if (state[jobId]) {
        const ids = state[jobId].assignedAgents.map((c) => c.agent_id);
        if (!ids.includes(assignment.agent_id)) {
          state[jobId].assignedAgents.push({
            isResponder: true,
            ...assignment,
          });
          markDirty(state[jobId]);
        }
      }
    },
    updateAssignedAgent: (
      state: JobEditorsState,
      action: PayloadAction<{
        jobId: string;
        id: string;
        props: Partial<JobAgentAssignment>;
      }>
    ) => {
      const { jobId, id, props } = action.payload;
      if (state[jobId]) {
        state[jobId].assignedAgents = state[jobId].assignedAgents.map((a) => {
          if (a.agent_id === id) {
            return {
              ...a,
              ...props,
            };
          }
          return a;
        });
        markDirty(state[jobId]);
      }
    },
    setUnassignAgent: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; agentId: string }>
    ) => {
      const { jobId, agentId } = action.payload;
      if (state[jobId]) {
        state[jobId].assignedAgents = state[jobId].assignedAgents.filter(
          (item) => item.agent_id !== agentId
        );
        markDirty(state[jobId]);
      }
    },

    // Suppression Lists assignment
    setIsNewSuppressionListCreated: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; isCreated: boolean }>
    ) => {
      const { jobId, isCreated } = action.payload;
      if (state[jobId]) {
        state[jobId].isNewSuppressionListCreated = isCreated;
      }
    },
    setAssignSuppressionList: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; assignment: JobSuppressionListAssignment }>
    ) => {
      const { jobId, assignment } = action.payload;
      if (state[jobId]) {
        const ids = state[jobId].assignedSuppressionLists.map(
          (c) => c.suppression_list_id
        );
        if (!ids.includes(assignment.suppression_list_id)) {
          state[jobId].assignedSuppressionLists.push(assignment);
          markDirty(state[jobId]);
        }
      }
    },
    setUnassignSuppressionList: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; suppressionListId: number }>
    ) => {
      const { jobId, suppressionListId } = action.payload;
      if (state[jobId]) {
        state[jobId].assignedSuppressionLists = state[
          jobId
        ].assignedSuppressionLists.filter(
          (item) => item.suppression_list_id !== suppressionListId
        );
        markDirty(state[jobId]);
      }
    },

    // Questions
    addNewQuestion: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; question: Partial<JobQuestion> }>
    ) => {
      const { jobId, question } = action.payload;
      if (state[jobId]) {
        const uid = uuidv4();
        const nextOrder = getNextQuestionOrder(state[jobId]);
        const newQuestion: JobQuestion = {
          id: uid,
          order: nextOrder,
          van_question: null,
          responses: [],
          title: '',
          template: '',
          controlType: 'number',
          ...question,
        };
        state[jobId].questions.push(newQuestion);
        markDirty(state[jobId]);
      }
    },
    updateQuestion: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; id: string; props: Partial<JobQuestion> }>
    ) => {
      const { jobId, id, props } = action.payload;
      if (state[jobId]) {
        state[jobId].questions = state[jobId].questions.map((question) => {
          if (question.id === id) {
            return {
              ...question,
              ...props,
            };
          }
          return question;
        });
        markDirty(state[jobId]);
      }
    },
    removeQuestion: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; questionId: string }>
    ) => {
      const { jobId, questionId } = action.payload;
      if (state[jobId]) {
        state[jobId].questions = state[jobId].questions.filter(
          (item) => item.id !== questionId
        );
        markDirty(state[jobId]);
      }
    },

    // Reply Templates
    addReplyTemplate: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; template: Omit<JobTemplate, 'id'> }>
    ) => {
      const { jobId, template } = action.payload;
      if (state[jobId]) {
        // Ensure we have original job data structure
        if (!state[jobId].originalJobData) {
          state[jobId].originalJobData = {} as JobDetailsResponse;
        }
        if (!state[jobId].originalJobData!.templates) {
          state[jobId].originalJobData!.templates = [];
        }

        const newTemplate: JobTemplate = {
          ...template,
          id: uuidv4(),
          is_default: false, // Reply templates are never default
        };

        state[jobId].originalJobData!.templates.push(newTemplate);
        markDirty(state[jobId]);
      }
    },
    updateReplyTemplate: (
      state: JobEditorsState,
      action: PayloadAction<{
        jobId: string;
        templateId: string;
        props: Partial<JobTemplate>;
      }>
    ) => {
      const { jobId, templateId, props } = action.payload;
      if (state[jobId] && state[jobId].originalJobData?.templates) {
        state[jobId].originalJobData!.templates = state[
          jobId
        ].originalJobData!.templates.map((template) => {
          if (template.id === templateId) {
            return {
              ...template,
              ...props,
            };
          }
          return template;
        });
        markDirty(state[jobId]);
      }
    },
    removeReplyTemplate: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; templateId: string }>
    ) => {
      const { jobId, templateId } = action.payload;
      if (state[jobId] && state[jobId].originalJobData?.templates) {
        state[jobId].originalJobData!.templates = state[
          jobId
        ].originalJobData!.templates.filter((template) => template.id !== templateId);
        markDirty(state[jobId]);
      }
    },

    // Validation
    updateValidationState: (
      state: JobEditorsState,
      action: PayloadAction<{
        jobId: string;
        validationState: {
          isValid: boolean;
          errors: {
            rootAccount?: string;
            jobName?: string;
            initialMessage?: string[];
            serverMessage?: string;
          };
          isValidating: boolean;
        };
      }>
    ) => {
      const { jobId, validationState } = action.payload;
      if (state[jobId]) {
        state[jobId].validationState = validationState;
      }
    },
    setValidationError: (
      state: JobEditorsState,
      action: PayloadAction<{
        jobId: string;
        field: 'rootAccount' | 'jobName' | 'serverMessage';
        error?: string;
      }>
    ) => {
      const { jobId, field, error } = action.payload;
      if (state[jobId]) {
        if (error) {
          state[jobId].validationState.errors[field] = error;
        } else {
          delete state[jobId].validationState.errors[field];
        }
        // Update overall validity
        const hasErrors = Object.keys(state[jobId].validationState.errors).length > 0;
        state[jobId].validationState.isValid =
          !hasErrors && !state[jobId].validationState.isValidating;
      }
    },
    setValidationLoading: (
      state: JobEditorsState,
      action: PayloadAction<{ jobId: string; isValidating: boolean }>
    ) => {
      const { jobId, isValidating } = action.payload;
      if (state[jobId]) {
        state[jobId].validationState.isValidating = isValidating;
        // If validating, mark as invalid temporarily
        if (isValidating) {
          state[jobId].validationState.isValid = false;
        }
      }
    },
  },
});

export const {
  initializeJobEditor,
  cleanupJobEditor,
  populateFromJobDetails,
  markClean,
  resetJobEditor,
  reset,
  setIdentityId,
  setJobType,
  setJobName,
  setJobArea,
  setDidNpaSubset,
  setBillingId,
  setActiveEntrySectionId,
  setIsShowingFloatingAttributes,
  setIsShowingFloatingEmoji,
  setIsShowingMediaManager,
  setIsAiEnabled,
  setAIOptOutThreshold,
  setUsesRateplan,
  // Contact Lists
  setAssignContactList,
  setUnassignContactList,
  // Agents
  setAssignAgent,
  setUnassignAgent,
  updateAssignedAgent,
  setAssignAgentAutomatically,
  setIsNewAgentCreated,
  // Suppression Lists
  setIsNewSuppressionListCreated,
  setAssignSuppressionList,
  setUnassignSuppressionList,
  // Questions
  addNewQuestion,
  updateQuestion,
  removeQuestion,
  // Tracked Links
  addTrackedLink,
  removeTrackedLink,
  updateTrackedLink,
  addLinkToInitialMessage,
  // Initial Message
  setInitialMessageMedia,
  setInitialMessageContent,
  setInitialMessageOptOutText,
  setInitialMessageMaxLength,
  setInitialMessageValidationMessages,
  // Reply Templates
  addReplyTemplate,
  updateReplyTemplate,
  removeReplyTemplate,
  // Validation
  updateValidationState,
  setValidationError,
  setValidationLoading,
} = jobEditorsSlice.actions;

export default jobEditorsSlice;
